import { useState } from "react";
import { useDispatch } from "react-redux";

import { OTHER_ROW_ID } from "@/constants/dataset";
import {
  toggleAreaAdminPopup,
  toggleCatchmentPopup,
  toggleDataWeightSettingsPopup,
  toggleGlobalFilterPopup
} from "@/store/slice/uiSlice";
import { ColorTarget, RowMarkerFields } from "@/types/dataset";

type LegendRowLike = RowMarkerFields & {
  id: string;
  fillColor: string;
  outlineColor: string;
};

/**
 * Sub-panel state for a legend config (color/image picker + row edit panel):
 * keeps at most one panel open and derives the active row and its current color.
 */
export const useLegendSubPanels = <Row extends LegendRowLike>({
  disabled,
  rows,
  otherFill,
  otherOutline
}: {
  disabled: boolean;
  rows: Row[];
  otherFill: string;
  otherOutline: string;
}) => {
  const dispatch = useDispatch();
  const [activeColor, setActiveColor] = useState<ColorTarget | null>(null);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);

  const closeOtherPanels = () => {
    dispatch(toggleDataWeightSettingsPopup(false));
    dispatch(toggleCatchmentPopup(false));
    dispatch(toggleAreaAdminPopup(false));
    dispatch(toggleGlobalFilterPopup(false));
  };

  const openColor = (rowId: string, target: ColorTarget["target"]) => {
    if (disabled) return;
    setEditingRowId(null);
    setActiveColor({ rowId, target });
    closeOtherPanels();
  };

  const openEdit = (rowId: string) => {
    if (disabled) return;
    setActiveColor(null);
    setEditingRowId(rowId);
    closeOtherPanels();
  };

  // The active ring must reflect the specific swatch (fill/outline), not just
  // the row — otherwise clicking the outline swatch keeps the ring on fill.
  const isActiveSwatch = (rowId: string, targets: ColorTarget["target"][]) =>
    activeColor?.rowId === rowId && targets.includes(activeColor.target);

  const activeRow =
    activeColor?.rowId === OTHER_ROW_ID
      ? null
      : (rows.find((r) => r.id === activeColor?.rowId) ?? null);
  const isOutlineTarget = activeColor?.target === "outline";
  const otherColorValue = isOutlineTarget ? otherOutline : otherFill;
  const rowColorValue = isOutlineTarget
    ? activeRow?.outlineColor
    : activeRow?.fillColor;
  const activeColorValue =
    activeColor?.rowId === OTHER_ROW_ID ? otherColorValue : rowColorValue;

  return {
    activeColor,
    setActiveColor,
    editingRowId,
    setEditingRowId,
    openColor,
    openEdit,
    isActiveSwatch,
    activeRow,
    activeColorValue
  };
};
